import { React, useState, useEffect, useRef } from "react";
import AppWrapper from "../AppWrapper";
import "./Prices.css";
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
} from "@mui/material";
import {
  Page,
  Button,
  BlockStack,
  ButtonGroup,
  Text,
  LegacyCard,
  Select,
  Modal,
  TextField,
  Checkbox,
  InlineStack,
  Badge,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import * as XLSX from "xlsx/xlsx.mjs";
import { applyColumnFormats, MONEY_FMT } from "../../utility/xlsxExport";

const DAY_COLS = ["1", "2-3", "4-6", "7-14", "15-30"];

function Prices() {
  const [pricesData, setPricesData] = useState({ prices: [], edited: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Multiple price lists: a selector picks which list's grid we view/edit.
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [listModal, setListModal] = useState({
    open: false,
    mode: "create",
    id: null,
    title: "",
    priority: "100",
    is_active: true,
    is_public: false,
  });
  const [listSaving, setListSaving] = useState(false);

  const loadLists = async (preferId) => {
    const { data } = await supabase
      .from("price_lists")
      .select("*")
      .order("priority", { ascending: true })
      .order("id", { ascending: true });
    const arr = data || [];
    setLists(arr);
    setSelectedListId((cur) => preferId ?? cur ?? arr[0]?.id ?? null);
    return arr;
  };

  const loadPrices = async (listId) => {
    if (!listId) {
      setPricesData({ edited: [], prices: [] });
      return;
    }
    const { data } = await supabase
      .from("prices")
      .select("car_models(show_as, name, car_brands(name), created_at), *")
      .eq("price_list_id", listId)
      .order("car_models(created_at)", { ascending: false });
    setPricesData({ edited: [], prices: data || [] });
  };

  const openCreateList = () =>
    setListModal({
      open: true,
      mode: "create",
      id: null,
      title: "",
      priority: String((lists[lists.length - 1]?.priority || 100) + 10),
      is_active: true,
      is_public: false,
    });

  const openConfigList = () => {
    const l = lists.find((x) => x.id === selectedListId);
    if (!l) return;
    setListModal({
      open: true,
      mode: "edit",
      id: l.id,
      title: l.title,
      priority: String(l.priority),
      is_active: l.is_active,
      is_public: l.is_public,
    });
  };

  const saveList = async () => {
    const payload = {
      title: listModal.title.trim(),
      priority: parseInt(listModal.priority) || 100,
      is_active: listModal.is_active,
      is_public: listModal.is_public,
    };
    if (!payload.title) {
      alert("El título es obligatorio.");
      return;
    }
    setListSaving(true);
    try {
      // Only one list may be public; clear the flag elsewhere first.
      if (payload.is_public) {
        await supabase
          .from("price_lists")
          .update({ is_public: false })
          .eq("is_public", true)
          .neq("id", listModal.id ?? -1);
      }
      if (listModal.mode === "create") {
        const { data, error } = await supabase
          .from("price_lists")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw new Error(error.message);
        // Seed the new list by copying the currently selected list's grid so
        // the user starts from a full table they can adjust (via Excel).
        if (selectedListId) {
          const { data: src } = await supabase
            .from("prices")
            .select("*")
            .eq("price_list_id", selectedListId);
          const rows = (src || []).map(({ price_list_id, ...r }) => ({
            ...r,
            price_list_id: data.id,
          }));
          if (rows.length) {
            const { error: e2 } = await supabase.from("prices").insert(rows);
            if (e2) alert("Lista creada, pero falló copiar los precios: " + e2.message);
          }
        }
        setListModal((m) => ({ ...m, open: false }));
        await loadLists(data.id);
      } else {
        const { error } = await supabase
          .from("price_lists")
          .update(payload)
          .eq("id", listModal.id);
        if (error) throw new Error(error.message);
        setListModal((m) => ({ ...m, open: false }));
        await loadLists(listModal.id);
      }
    } catch (e) {
      alert("Error al guardar la lista: " + (e?.message || e));
    } finally {
      setListSaving(false);
    }
  };

  const modelLabel = (p) =>
    `${p.car_models?.show_as || p.car_models?.name} (${p.car_models?.car_brands?.name || ""})`;

  const downloadExcel = () => {
    const rows = [
      ["Modelo", "Tarifa", "Precio 1 día", "Precio 2-3 días", "Precio 4-6 días", "Precio 7-14 días", "Precio 15-30 días", "Depósito Garantía"],
    ];
    for (const p of pricesData.prices) {
      const label = modelLabel(p);
      const days = DAY_COLS;
      // Básica = base
      rows.push([label, "Básica 10%", ...days.map((d) => p[`base_${d}`] || 0), p.deposit_base || 0]);
      // Estandar = base + standard
      rows.push([label, "Estandar 5%", ...days.map((d) => (p[`base_${d}`] || 0) + (p[`standard_${d}`] || 0)), p.deposit_standard || 0]);
      // Premium = base + premium
      rows.push([label, "Premium 0%", ...days.map((d) => (p[`base_${d}`] || 0) + (p[`premium_${d}`] || 0)), p.deposit_premium || 0]);
      // Efectivo = base + premium + cash
      rows.push([label, "Efectivo 0%", ...days.map((d) => (p[`base_${d}`] || 0) + (p[`premium_${d}`] || 0) + (p[`cash_${d}`] || 0)), p.deposit_premium || 0]);
      // Foraneo = same as Premium price, different deposit
      rows.push([label, "Foraneo 0%", ...days.map((d) => (p[`base_${d}`] || 0) + (p[`premium_${d}`] || 0)), p.deposit_foreign || 0]);
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    // Everything after Modelo / Tarifa is money (the DAY_COLS price columns
    // plus the deposit). Numeric cells with a currency format keep the sheet
    // summable, and the re-import below reads the numbers back unchanged.
    applyColumnFormats(
      ws,
      Object.fromEntries(
        Array.from({ length: DAY_COLS.length + 1 }, (_, i) => [i + 2, MONEY_FMT])
      )
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tarifas");
    // Name the file after the selected list so the two lists' exports don't
    // collide (upload always targets the currently selected list).
    const list = lists.find((l) => l.id === selectedListId);
    const safeTitle = (list?.title || "Lista").replace(/[\\/:*?"<>|]/g, "-");
    XLSX.writeFile(wb, `Tarifas - ${safeTitle}.xlsx`);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      // Skip header row
      const dataRows = rows.slice(1).filter((r) => r[0] && r[1]);

      // Group by model label
      const byModel = {};
      for (const r of dataRows) {
        const label = String(r[0]).trim();
        const tier = String(r[1]).trim();
        if (!byModel[label]) byModel[label] = {};
        byModel[label][tier] = {
          prices: [r[2], r[3], r[4], r[5], r[6]].map((v) => parseFloat(v) || 0),
          deposit: parseFloat(r[7]) || 0,
        };
      }

      // Map label → price row id
      const labelToId = {};
      for (const p of pricesData.prices) {
        labelToId[modelLabel(p)] = p.id;
      }

      let updates = 0;
      let skipped = [];
      for (const [label, tiers] of Object.entries(byModel)) {
        const id = labelToId[label];
        if (!id) { skipped.push(label); continue; }

        const basica = tiers["Básica 10%"];
        const estandar = tiers["Estandar 5%"];
        const premium = tiers["Premium 0%"];
        const efectivo = tiers["Efectivo 0%"];
        const foraneo = tiers["Foraneo 0%"];
        if (!basica || !estandar || !premium || !efectivo) {
          skipped.push(`${label} (filas incompletas)`);
          continue;
        }

        const update = {};
        DAY_COLS.forEach((d, i) => {
          update[`base_${d}`] = basica.prices[i];
          update[`standard_${d}`] = estandar.prices[i] - basica.prices[i];
          update[`premium_${d}`] = premium.prices[i] - basica.prices[i];
          update[`cash_${d}`] = efectivo.prices[i] - premium.prices[i];
        });
        update.deposit_base = basica.deposit;
        update.deposit_standard = estandar.deposit;
        update.deposit_premium = premium.deposit;
        if (foraneo) update.deposit_foreign = foraneo.deposit;

        const { error } = await supabase
          .from("prices")
          .update(update)
          .eq("id", id)
          .eq("price_list_id", selectedListId);
        if (error) {
          alert(`Error en ${label}: ${error.message}`);
          setUploading(false);
          return;
        }
        updates++;
      }

      alert(`Actualizado: ${updates} modelos. ${skipped.length > 0 ? `Saltados: ${skipped.join(", ")}` : ""}`);
      // Reload the current list's grid.
      await loadPrices(selectedListId);
    } catch (err) {
      alert("Error al procesar el archivo: " + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handlePriceChange = (e) => {
    const [rowId, indexStr, column] = e.target.id.split("__");
    const index = parseInt(indexStr);
    const newPrices = [...pricesData.prices];
    newPrices[index] = { ...newPrices[index], [column]: parseFloat(e.target.value) || 0 };
    const newEdited = pricesData.edited.includes(parseInt(rowId))
      ? pricesData.edited
      : [...pricesData.edited, parseInt(rowId)];
    setPricesData({ prices: newPrices, edited: newEdited });
  };

  const savePrices = async () => {
    setSaving(true);
    for (const rowId of pricesData.edited) {
      const row = pricesData.prices.find((p) => p.id === rowId);
      if (!row) continue;
      const { car_models, price_list_id, ...priceFields } = row;
      const { error } = await supabase
        .from("prices")
        .update(priceFields)
        .eq("id", rowId)
        .eq("price_list_id", selectedListId);
      if (error) {
        alert("Error guardando precios: " + error.message);
        setSaving(false);
        return;
      }
    }
    setPricesData({ ...pricesData, edited: [] });
    setSaving(false);
  };

  useEffect(() => {
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedListId) loadPrices(selectedListId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListId]);

  return (
    <AppWrapper className="prices_wrapper">
      <Page
        title="Tabulador de Precios"
        primaryAction={
          pricesData.edited.length > 0
            ? {
                content: saving
                  ? "Guardando..."
                  : `Guardar Cambios (${pricesData.edited.length})`,
                onAction: savePrices,
                loading: saving,
                disabled: saving,
              }
            : undefined
        }
        secondaryActions={
          <ButtonGroup>
            <Button onClick={openCreateList}>Nueva lista</Button>
            <Button onClick={openConfigList} disabled={!selectedListId}>
              Configurar lista
            </Button>
            <Button onClick={downloadExcel}>Descargar Excel</Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              loading={uploading}
              disabled={uploading}
            >
              Subir Excel
            </Button>
          </ButtonGroup>
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        <BlockStack gap="400">
          <LegacyCard>
            <div style={{ padding: "12px 16px" }}>
              <InlineStack gap="300" blockAlign="end" wrap={false}>
                <div style={{ minWidth: 280 }}>
                  <Select
                    label="Lista de precios"
                    options={lists.map((l) => ({
                      label: `${l.title}${l.is_public ? " · Pública" : ""}${l.is_active ? "" : " · inactiva"}`,
                      value: String(l.id),
                    }))}
                    value={selectedListId ? String(selectedListId) : ""}
                    onChange={(v) => setSelectedListId(parseInt(v))}
                  />
                </div>
                {(() => {
                  const l = lists.find((x) => x.id === selectedListId);
                  if (!l) return null;
                  return (
                    <InlineStack gap="150">
                      {l.is_public && <Badge tone="info">Pública</Badge>}
                      {!l.is_active && <Badge tone="critical">Inactiva</Badge>}
                    </InlineStack>
                  );
                })()}
              </InlineStack>
            </div>
          </LegacyCard>
          <Text variant="headingMd" as="h3">Precio Base</Text>
          <LegacyCard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell sx={{ textAlign: "center" }}>1 día</TableCell>
              <TableCell sx={{ textAlign: "center" }}>2-3 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>4-6 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>7-14 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>15-30 días</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pricesData.prices.map((p, index) => (
              <TableRow className="prices_row" key={p.car_models.name}>
                <TableCell>
                  {p.car_models.show_as || p.car_models.name} (
                  {p.car_models.car_brands.name})
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__base_1`}
                    value={p["base_1"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__base_2-3`}
                    value={p["base_2-3"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__base_4-6`}
                    value={p["base_4-6"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__base_7-14`}
                    value={p["base_7-14"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__base_15-30`}
                    value={p["base_15-30"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          </LegacyCard>
          <Text variant="headingMd" as="h3">Precio Standard</Text>
          <LegacyCard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell sx={{ textAlign: "center" }}>1 día</TableCell>
              <TableCell sx={{ textAlign: "center" }}>2-3 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>4-6 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>7-14 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>15-30 días</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pricesData.prices.map((p, index) => (
              <TableRow className="prices_row" key={p.car_models.name}>
                <TableCell>
                  {p.car_models.show_as || p.car_models.name} (
                  {p.car_models.car_brands.name})
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__standard_1`}
                    value={p["standard_1"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__standard_2-3`}
                    value={p["standard_2-3"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__standard_4-6`}
                    value={p["standard_4-6"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__standard_7-14`}
                    value={p["standard_7-14"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__standard_15-30`}
                    value={p["standard_15-30"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          </LegacyCard>
          <Text variant="headingMd" as="h3">Precio Premium</Text>
          <LegacyCard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell sx={{ textAlign: "center" }}>1 día</TableCell>
              <TableCell sx={{ textAlign: "center" }}>2-3 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>4-6 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>7-14 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>15-30 días</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pricesData.prices.map((p, index) => (
              <TableRow className="prices_row" key={p.car_models.name}>
                <TableCell>
                  {p.car_models.show_as || p.car_models.name} (
                  {p.car_models.car_brands.name})
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__premium_1`}
                    value={p["premium_1"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__premium_2-3`}
                    value={p["premium_2-3"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__premium_4-6`}
                    value={p["premium_4-6"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__premium_7-14`}
                    value={p["premium_7-14"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__premium_15-30`}
                    value={p["premium_15-30"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          </LegacyCard>
          <Text variant="headingMd" as="h3">Costo Efectivo</Text>
          <LegacyCard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell sx={{ textAlign: "center" }}>1 día</TableCell>
              <TableCell sx={{ textAlign: "center" }}>2-3 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>4-6 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>7-14 días</TableCell>
              <TableCell sx={{ textAlign: "center" }}>15-30 días</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pricesData.prices.map((p, index) => (
              <TableRow className="prices_row" key={p.car_models.name}>
                <TableCell>
                  {p.car_models.show_as || p.car_models.name} (
                  {p.car_models.car_brands.name})
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__cash_1`}
                    value={p["cash_1"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__cash_2-3`}
                    value={p["cash_2-3"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__cash_4-6`}
                    value={p["cash_4-6"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__cash_7-14`}
                    value={p["cash_7-14"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__cash_15-30`}
                    value={p["cash_15-30"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          </LegacyCard>
          <Text variant="headingMd" as="h3">Depósito</Text>
          <LegacyCard>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Modelo</TableCell>
              <TableCell sx={{ textAlign: "center" }}>Base</TableCell>
              <TableCell sx={{ textAlign: "center" }}>Standard</TableCell>
              <TableCell sx={{ textAlign: "center" }}>Premium</TableCell>
              <TableCell sx={{ textAlign: "center" }}>Foráneo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pricesData.prices.map((p, index) => (
              <TableRow className="prices_row" key={p.car_models.name}>
                <TableCell>
                  {p.car_models.show_as || p.car_models.name} (
                  {p.car_models.car_brands.name})
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__deposit_base`}
                    value={p["deposit_base"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__deposit_standard`}
                    value={p["deposit_standard"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__deposit_premium`}
                    value={p["deposit_premium"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
                <TableCell align="center">
                  <span className="dollar_sign">$ </span>
                  <input
                    type="number"
                    id={`${p.id}__${index}__deposit_foreign`}
                    value={p["deposit_foreign"]}
                    onChange={handlePriceChange}
                    disabled
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          </LegacyCard>
        </BlockStack>
        <Modal
          open={listModal.open}
          onClose={() => setListModal((m) => ({ ...m, open: false }))}
          title={listModal.mode === "create" ? "Nueva lista de precios" : "Configurar lista"}
          primaryAction={{
            content: listModal.mode === "create" ? "Crear (copiar actual)" : "Guardar",
            onAction: saveList,
            loading: listSaving,
            disabled: listSaving || !listModal.title.trim(),
          }}
          secondaryActions={[
            { content: "Cancelar", onAction: () => setListModal((m) => ({ ...m, open: false })) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <TextField
                label="Título"
                value={listModal.title}
                onChange={(v) => setListModal((m) => ({ ...m, title: v }))}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label="Prioridad"
                type="number"
                value={listModal.priority}
                onChange={(v) => setListModal((m) => ({ ...m, priority: v }))}
                helpText="Menor número = mayor prioridad. La lista activa de mayor prioridad es la preseleccionada en reservaciones y cotizaciones."
                autoComplete="off"
              />
              <Checkbox
                label="Activa"
                checked={listModal.is_active}
                onChange={(v) => setListModal((m) => ({ ...m, is_active: v }))}
              />
              <Checkbox
                label="Pública (sitio web y bot)"
                checked={listModal.is_public}
                onChange={(v) => setListModal((m) => ({ ...m, is_public: v }))}
                helpText="Solo una lista puede ser la pública; al activarla aquí se desactiva en las demás."
              />
              {listModal.mode === "create" && (
                <Text tone="subdued">
                  La nueva lista se crea copiando los precios de la lista seleccionada actualmente; luego puedes ajustarla con el Excel.
                </Text>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default Prices;
